import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { JourneyReportsService } from './journeyReportsApi';

/* ── Thunks ── */

export const tryToFetchHodReport = createAsyncThunk<any, { tripId: string; type: 'cabin' | 'bar' | 'restaurant' }>(
    'journeyReports/fetchHodReport',
    async ({ tripId, type }, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.fetchHodReport(tripId, type);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to fetch HOD report');
        }
    },
);

export const tryToSaveHodReport = createAsyncThunk<any, { tripId: string; type: 'cabin' | 'bar' | 'restaurant'; sections: Array<{ cabinId: string; editedSummary: string }> }>(
    'journeyReports/saveHodReport',
    async ({ tripId, type, sections }, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.saveHodReport(tripId, type, { sections });
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to save HOD report');
        }
    },
);

export const tryToFetchTmReport = createAsyncThunk<any, string>(
    'journeyReports/fetchTmReport',
    async (tripId, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.fetchTmReport(tripId);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to fetch TM report');
        }
    },
);

export const tryToSaveTmReport = createAsyncThunk<any, { tripId: string; data: { sections?: Array<{ cabinId: string; editedSummary: string }>; editedDraft?: string } }>(
    'journeyReports/saveTmReport',
    async ({ tripId, data }, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.saveTmReport(tripId, data);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to save TM report');
        }
    },
);

export const tryToExportDocx = createAsyncThunk<any, string>(
    'journeyReports/exportDocx',
    async (tripId, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.exportDocx(tripId);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to export DOCX');
        }
    },
);

export const tryToExportPdf = createAsyncThunk<any, string>(
    'journeyReports/exportPdf',
    async (tripId, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.exportPdf(tripId);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to export PDF');
        }
    },
);

export const tryToFetchHistorical = createAsyncThunk<any, { page?: number; size?: number }>(
    'journeyReports/fetchHistorical',
    async (params, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.fetchHistorical(params);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to fetch historical reports');
        }
    },
);

export const tryToFetchHistoricalDetail = createAsyncThunk<any, string>(
    'journeyReports/fetchHistoricalDetail',
    async (tripId, { rejectWithValue }) => {
        try {
            const res = await JourneyReportsService.fetchHistoricalDetail(tripId);
            return res?.data?.data ?? res?.data;
        } catch (err: any) {
            return rejectWithValue(err?.response?.data?.message || 'Failed to fetch report detail');
        }
    },
);

/* ── Slice ── */

const initialState: any = {
    historicalAreLoading: false,
    historical: null,
    hodReportLoading: false,
    tmReportLoading: false,
};

export const journeyReportsSlice = createSlice({
    name: 'journeyReports',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(tryToFetchHistorical.pending, (state) => {
                state.historicalAreLoading = true;
            })
            .addCase(tryToFetchHistorical.rejected, (state) => {
                state.historicalAreLoading = false;
            })
            .addCase(tryToFetchHistorical.fulfilled, (state, action) => {
                state.historicalAreLoading = false;
                state.historical = action.payload;
            })
            .addCase(tryToFetchHodReport.pending, (state) => {
                state.hodReportLoading = true;
            })
            .addCase(tryToFetchHodReport.rejected, (state) => {
                state.hodReportLoading = false;
            })
            .addCase(tryToFetchHodReport.fulfilled, (state) => {
                state.hodReportLoading = false;
            })
            .addCase(tryToFetchTmReport.pending, (state) => {
                state.tmReportLoading = true;
            })
            .addCase(tryToFetchTmReport.rejected, (state) => {
                state.tmReportLoading = false;
            })
            .addCase(tryToFetchTmReport.fulfilled, (state) => {
                state.tmReportLoading = false;
            });
    },
});

export default journeyReportsSlice.reducer;
